export type DeadlinePresetId =
  | 'last_lot_end'
  | 'day_before_event'
  | 'hour_before_event'
  | 'at_event_start';

export interface DeadlineContext {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  ticketCatalogMode?: 'legacy' | 'batched' | 'phased' | string;
  salesPhases?: Array<{ endSaleDate?: Date | string | null; name?: string }>;
  tickets?: Array<{ endSaleDate?: Date | string | null }>;
  ticketTypes?: Array<{ batches?: Array<{ endSaleDate?: Date | string | null }> }>;
}

export interface DeadlinePreset {
  id: DeadlinePresetId;
  label: string;
  hint: string;
  recommended?: boolean;
  adjusted?: boolean;
}

function toDate(value?: Date | string | null): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isValidRegistrationDeadline(value: unknown, startDate?: Date | string | null): boolean {
  if (!value) return false;
  const d = toDate(value as Date | string);
  if (!d || d.getFullYear() < 2000) return false;
  const start = toDate(startDate);
  if (start && d.getTime() > start.getTime()) return false;
  return true;
}

export function formatDeadlineLabel(value?: Date | string | null): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Prazo válido: no máximo no início do evento (inscrições fecham antes ou no começo). */
export function clampDeadlineBeforeEventStart(
  date: Date,
  startDate?: Date | string | null
): { date: Date; adjusted: boolean } {
  const start = toDate(startDate);
  if (!start || date.getTime() <= start.getTime()) {
    return { date, adjusted: false };
  }
  return { date: new Date(start.getTime() - 3600000), adjusted: true };
}

export function getLastSalesEndDate(ctx: DeadlineContext): Date | null {
  const candidates: Date[] = [];

  if (ctx.ticketCatalogMode === 'phased' && ctx.salesPhases?.length) {
    ctx.salesPhases.forEach((phase) => {
      const d = toDate(phase.endSaleDate);
      if (d) candidates.push(d);
    });
  } else if (ctx.ticketCatalogMode === 'batched' && ctx.ticketTypes?.length) {
    ctx.ticketTypes.forEach((type) => {
      type.batches?.forEach((batch) => {
        const d = toDate(batch.endSaleDate);
        if (d) candidates.push(d);
      });
    });
  } else if (ctx.tickets?.length) {
    ctx.tickets.forEach((ticket) => {
      const d = toDate(ticket.endSaleDate);
      if (d) candidates.push(d);
    });
  }

  if (candidates.length === 0) return null;
  return new Date(Math.max(...candidates.map((d) => d.getTime())));
}

export function isLastLotEndAfterEventStart(ctx: DeadlineContext): boolean {
  const start = toDate(ctx.startDate);
  const lastSalesEnd = getLastSalesEndDate(ctx);
  if (!start || !lastSalesEnd) return false;
  return lastSalesEnd.getTime() > start.getTime();
}

export function getRecommendedDeadline(ctx: DeadlineContext): {
  date: Date | null;
  presetId: DeadlinePresetId | null;
  reason: string;
} {
  const start = toDate(ctx.startDate);
  const lastSalesEnd = getLastSalesEndDate(ctx);
  const usesLots = ctx.ticketCatalogMode === 'phased' || ctx.ticketCatalogMode === 'batched';

  if (lastSalesEnd && usesLots && start) {
    const clamped = clampDeadlineBeforeEventStart(lastSalesEnd, start);
    if (!clamped.adjusted) {
      return {
        date: clamped.date,
        presetId: 'last_lot_end',
        reason: 'Igual ao fim do último lote',
      };
    }
  }

  if (start) {
    const oneHourBefore = new Date(start.getTime() - 3600000);
    return {
      date: oneHourBefore,
      presetId: 'hour_before_event',
      reason: usesLots && lastSalesEnd
        ? '1 h antes do evento (o fim do último lote é depois do início — ajuste os lotes ou use esta sugestão)'
        : '1 hora antes do início do evento',
    };
  }

  return { date: null, presetId: null, reason: '' };
}

export function buildDeadlinePresets(ctx: DeadlineContext): Array<DeadlinePreset & { date: Date | null }> {
  const start = toDate(ctx.startDate);
  const lastSalesEnd = getLastSalesEndDate(ctx);
  const recommended = getRecommendedDeadline(ctx);

  const presets: Array<DeadlinePreset & { date: Date | null }> = [];

  if (lastSalesEnd && start) {
    const clamped = clampDeadlineBeforeEventStart(lastSalesEnd, start);
    presets.push({
      id: 'last_lot_end',
      label: clamped.adjusted ? 'Fim do último lote' : 'Fim do último lote',
      hint: clamped.adjusted
        ? `${formatDeadlineLabel(lastSalesEnd)} → ${formatDeadlineLabel(clamped.date)}`
        : formatDeadlineLabel(clamped.date),
      recommended: recommended.presetId === 'last_lot_end',
      adjusted: clamped.adjusted,
      date: clamped.date,
    });
  } else if (lastSalesEnd) {
    presets.push({
      id: 'last_lot_end',
      label: 'Fim do último lote',
      hint: formatDeadlineLabel(lastSalesEnd),
      recommended: recommended.presetId === 'last_lot_end',
      date: lastSalesEnd,
    });
  }

  if (start) {
    const dayBefore = new Date(start);
    dayBefore.setDate(dayBefore.getDate() - 1);
    dayBefore.setHours(23, 59, 0, 0);

    presets.push({
      id: 'day_before_event',
      label: 'Véspera do evento',
      hint: formatDeadlineLabel(dayBefore),
      recommended: recommended.presetId === 'day_before_event',
      date: dayBefore,
    });

    const hourBefore = new Date(start.getTime() - 3600000);
    presets.push({
      id: 'hour_before_event',
      label: '1 h antes do evento',
      hint: formatDeadlineLabel(hourBefore),
      recommended: recommended.presetId === 'hour_before_event',
      date: hourBefore,
    });
  }

  return presets.filter((p) => p.date && isValidRegistrationDeadline(p.date, ctx.startDate));
}

export function getEventDatesSummary(ctx: DeadlineContext): string | null {
  const start = toDate(ctx.startDate);
  const end = toDate(ctx.endDate);
  if (!start) return null;
  if (end) {
    return `Evento: ${formatDeadlineLabel(start)} → ${formatDeadlineLabel(end)}`;
  }
  return `Evento começa em ${formatDeadlineLabel(start)}`;
}

export function getLotScheduleWarning(ctx: DeadlineContext): string | null {
  if (!isLastLotEndAfterEventStart(ctx)) return null;
  const start = toDate(ctx.startDate);
  const lastEnd = getLastSalesEndDate(ctx);
  if (!start || !lastEnd) return null;
  return `Atenção: o último lote termina em ${formatDeadlineLabel(lastEnd)}, depois do início do evento (${formatDeadlineLabel(start)}). Ajuste os lotes na aba Ingressos ou use uma sugestão válida abaixo.`;
}
